import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import {
  AuraAuthGuard,
  PostLoginRedirectGuard,
  CdhOutageGuard,
  VerifyEmailGuard,
  UrlTokenGuardService,
  LoginPageGuard,
} from '@digital-platform/aura-authentication/guards';
import { RedirectGuard } from '@digital-platform/aura-authentication/guards';
import { AbandonCartGuard } from '@digital-platform/rs-cart/services';
import { locationPagesGuard } from '@digital-platform/rs-frontend/locations-redirect';
import {
  DOTCOM_SIGNUP_ROUTE,
  COR1_SIGNUP_ROUTE,
  COR2_SIGNUP_ROUTE,
  APP_SWITCHER_ROUTE,
} from '@digital-platform/rs-signup/domain';
import { ShellComponent } from './shell/shell.component';
import { v1ProtectedRoutes } from './v1-protected-routes';

const routes: Routes = [
  {
    path: '',
    component: ShellComponent,
    canActivate: [UrlTokenGuardService, RedirectGuard],
    children: [
      // @AURA - Need to catch the # as a temp fix for %23 in the url sending us to 404 as it falls back to v1. We need to investigate why angular in v1 or v2 is suddenly sending us to that route.
      {
        path: '#',
        redirectTo: '/',
      },
      {
        path: 'playground',
        loadChildren: () =>
          import('@digital-platform/rs-frontend/feature-playground').then(
            (m) => m.RsFrontendFeaturePlaygroundModule
          ),
      },
      {
        path: 'facilities',
        loadChildren: () =>
          import('@digital-platform/rs-frontend/feature-facilities').then(
            (m) => m.RsFrontendFeatureFacilitiesModule
          ),
      },
      {
        path: 'about-us',
        loadChildren: () =>
          import('@digital-platform/rs-frontend/feature-about-us').then(
            (m) => m.RsFrontendFeatureAboutUsModule
          ),
      },
      {
        path: 'contact-us',
        loadChildren: () =>
          import('@digital-platform/rs-frontend/feature-contact').then(
            (m) => m.RsFrontendFeatureContactUsModule
          ),
      },
      {
        path: APP_SWITCHER_ROUTE,
        canActivate: [AuraAuthGuard, VerifyEmailGuard],
        loadChildren: () =>
          import('@digital-platform/rs-frontend/feature-app-switcher').then(
            (m) => m.RsFrontendFeatureAppSwitcherModule
          ),
      },
      {
        path: 'auth',
        canActivate: [VerifyEmailGuard, PostLoginRedirectGuard],
        loadChildren: () =>
          import('@digital-platform/rs-signup/feature-authcallback').then(
            (m) => m.rsSignupFeatureAuthcallbackRoutes
          ),
      },
      {
        path: 'universal-login',
        loadComponent: () =>
          import('@digital-platform/rs-signup/feature-login').then(
            (m) => m.LoginComponent
          ),
      },
      {
        path: 'account/login',
        data: { page: 'login' },
        canActivate: [LoginPageGuard, CdhOutageGuard],
        loadComponent: () =>
          import('@digital-platform/rs-signup/feature-auth').then(
            (m) => m.AuthComponent
          ),
      },
      {
        path: 'account/signup',
        data: { page: 'signup' },
        loadComponent: () =>
          import('@digital-platform/rs-signup/feature-auth').then(
            (m) => m.AuthComponent
          ),
      },
      {
        path: DOTCOM_SIGNUP_ROUTE,
        canActivate: [AuraAuthGuard, VerifyEmailGuard, CdhOutageGuard],
        data: {
          page: 'signup-rw',
        },
        loadChildren: () =>
          import('@digital-platform/rs-signup/feature-dotcom').then(
            (m) => m.rsSignupFeatureDotcomRoutes
          ),
      },
      {
        path: COR1_SIGNUP_ROUTE,
        canActivate: [AuraAuthGuard, VerifyEmailGuard],
        loadChildren: () =>
          import('@digital-platform/rs-signup/feature-cor1').then(
            (m) => m.rsSignupFeatureCor1Routes
          ),
      },
      {
        path: COR2_SIGNUP_ROUTE,
        canActivate: [AuraAuthGuard, VerifyEmailGuard],
        loadChildren: () =>
          import('@digital-platform/rs-signup/feature-cor2').then(
            (m) => m.rsSignupFeatureCor2Routes
          ),
      },
      {
        path: 'account/outage',
        loadComponent: () =>
          import('@digital-platform/rs-frontend/feature-outage-page').then(
            (m) => m.OutagePageComponent
          ),
      },

      // Moved in from v1, login-routing.module
      { path: 'login-popup', redirectTo: '/account/login' },

      // Grab all the protected routes, guard them here, then allow them to render the fallback
      ...v1ProtectedRoutes,

      {
        path: '**',
        canDeactivate: [AbandonCartGuard],
        canActivate: [locationPagesGuard],
        runGuardsAndResolvers: 'always',
        loadChildren: () =>
          import('@digital-platform/rs-frontend/feature-fallback-page').then(
            (m) => m.ROUTES
          ),
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class RsFrontendShellRoutingModule {}
